import { useInView, useMotionValue, useSpring } from 'motion/react';
import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface NumberTickerProps extends React.ComponentPropsWithoutRef<'span'> {
  /** Target value to animate to. */
  value: number;
  /** Starting value. Default 0. */
  startValue?: number;
  /** Count direction. Default "up". */
  direction?: 'up' | 'down';
  /** Delay before animation starts (seconds). Default 0. */
  delay?: number;
  /** Decimal places to show. Default 0. */
  decimalPlaces?: number;
}

/**
 * Animated number counter that springs to a target value on scroll into view.
 *
 * ```tsx
 * <NumberTicker value={1250} /> → animates 0 → 1,250
 * <NumberTicker value={99.9} decimalPlaces={1} />
 * ```
 */
export function NumberTicker({
  value,
  startValue = 0,
  direction = 'up',
  delay = 0,
  decimalPlaces = 0,
  className,
  ...props
}: NumberTickerProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const motionValue = useMotionValue(direction === 'down' ? value : startValue);
  const springValue = useSpring(motionValue, { damping: 60, stiffness: 100 });
  const isInView = useInView(ref, { once: true, margin: '0px' });

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => {
        motionValue.set(direction === 'down' ? startValue : value);
      }, delay * 1000);
      return () => clearTimeout(timer);
    }
  }, [motionValue, isInView, delay, value, direction, startValue]);

  useEffect(
    () =>
      springValue.on('change', (latest) => {
        if (ref.current) {
          ref.current.textContent = Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
          }).format(Number(latest.toFixed(decimalPlaces)));
        }
      }),
    [springValue, decimalPlaces],
  );

  return (
    <span
      ref={ref}
      className={cn('inline-block tabular-nums tracking-wider', className)}
      {...props}
    >
      {startValue}
    </span>
  );
}
